import React, { useCallback } from 'react';
import type { OnDragEndResponder } from 'react-beautiful-dnd';

import { EPanelOpenMethod } from '@components/block-event-single/store';
import AddRuleButton from '@components/mapping-panel/components/AddRuleButton';
import MetadataRuleGroup from '@components/mapping-panel/components/RuleBuilder/MetadataRuleGroup';
import RuleGroup from '@components/mapping-panel/components/RuleBuilder/RuleGroup';
import { Wrapper } from '@components/mapping-panel/components/RuleBuilder/RuleGroup/styled';
import {
  DeleteAllButton,
  Footer,
  Title,
  TopBar,
} from '@components/mapping-panel/components/RuleBuilder/styled';
import { letters } from '@components/mapping-panel/constants/constant';
import {
  IActiveIndices,
  IItemActiveIndices,
  IMappingRules,
  IMetadataItemActiveIndices,
} from '@components/mapping-panel/store';
import { ERuleActionType } from '@components/mapping-panel/typed';

import { EInputType } from './KeyValueBuilder/typed';

interface IRuleBuilder {
  mode?: EPanelOpenMethod;
  title?: string;
  showTitle?: boolean;
  data: IMappingRules[];
  showAddNewEventRuleBtn?: boolean;
  activeIndices?: IActiveIndices;
  onChange: (
    orIndex: number,
    subAndIndex: number,
    subOrIndex: number,
    inputType: EInputType,
    value: string,
  ) => void;
  updateActiveIndices: (
    orIndex?: number,
    eventName?: boolean,
    ruleItems?: IItemActiveIndices[],
    metadataName?: boolean,
    metadataItems?: IMetadataItemActiveIndices[],
  ) => void;
  onRulesChange: (
    actionType: ERuleActionType,
    orIndex?: number,
    subAndIndex?: number,
    subOrIndex?: number,
    metadataKey?: string,
  ) => void;
  onDragEnd?: OnDragEndResponder;
  showPadding?: boolean;
}

const RuleBuilder: React.FC<IRuleBuilder> = (props: IRuleBuilder) => {
  const {
    mode = EPanelOpenMethod.CREATE,
    title,
    showTitle = true,
    data,
    showAddNewEventRuleBtn = true,
    activeIndices,
    onChange,
    updateActiveIndices,
    onRulesChange,
    onDragEnd,
    showPadding,
  } = props;
  const isViewMode = mode === EPanelOpenMethod.VIEW;

  const activeOrIndex = activeIndices?.orIndex;

  const handleDeleteRuleGroup = (orIndex: number) => {
    if (activeOrIndex !== undefined) {
      if (activeOrIndex === orIndex) {
        updateActiveIndices?.();
      } else if (activeOrIndex > orIndex) {
        updateActiveIndices?.(activeOrIndex - 1);
      }
    }
    onRulesChange?.(ERuleActionType.DELETE_RULE_GROUP, orIndex);
  };

  const handleDeleteMetadataGroup = (orIndex: number, subAndIndex: number) => {
    const activeSubAndIndex = activeIndices?.metadataItems?.[0]?.subAndIndex;
    if (activeSubAndIndex && activeOrIndex !== undefined) {
      if (activeSubAndIndex === subAndIndex) {
        updateActiveIndices?.(orIndex, false, []);
      } else if (activeSubAndIndex > subAndIndex) {
        updateActiveIndices?.(activeOrIndex, false, undefined, false, [{ subAndIndex }]);
      }
    }
    onRulesChange?.(ERuleActionType.DELETE_METADATA_GROUP, orIndex, subAndIndex);
  };

  const AddMetadataRuleButton = useCallback(({ disabled, onClick }) => {
    return (
      <Footer
        disabled={disabled}
        style={{
          border: 'none',
          borderTop: '1px solid rgba(0, 0, 0, 0.15)',
          borderRadius: 0,
        }}
        onClick={onClick}
      >
        <AddRuleButton
          text="Add Metadata Rule"
          disabled={disabled}
          textStyle={{ fontWeight: 500 }}
        />
      </Footer>
    );
  }, []);

  return (
    <div style={{ width: '100%' }}>
      {showTitle && (
        <TopBar>
          <Title>{title}</Title>
          {!isViewMode && (
            <DeleteAllButton onClick={() => onRulesChange?.(ERuleActionType.DELETE_ALL)}>
              Delete All
            </DeleteAllButton>
          )}
        </TopBar>
      )}
      {data.map((ruleGroup, orIndex) => {
        const ruleGroupLabel = orIndex + 1;
        const disabledAddMetadataRuleButton =
          !ruleGroup.eventName || (ruleGroup?.metadata ?? []).length >= letters.length;
        return (
          <Wrapper
            active={activeOrIndex === orIndex && !isViewMode}
            key={`ruleGroupLabelWrapper_${ruleGroupLabel}`}
          >
            <RuleGroup
              mode={mode}
              activeIndices={activeIndices}
              data={ruleGroup}
              orIndex={orIndex}
              onDeleteRuleGroup={() => handleDeleteRuleGroup(orIndex)}
              onDeleteRuleItem={(subAndIndex, subOrIndex) =>
                onRulesChange?.(ERuleActionType.DELETE_RULE_ITEM, orIndex, subAndIndex, subOrIndex)
              }
              onAddAndRuleItem={() => onRulesChange?.(ERuleActionType.ADD_AND_RULE_ITEM, orIndex)}
              onAddOrRuleItem={() => onRulesChange?.(ERuleActionType.ADD_OR_RULE_ITEM, orIndex)}
              onChange={(subAndIndex, subOrIndex, inputType, value) =>
                onChange?.(orIndex, subAndIndex, subOrIndex, inputType, value)
              }
              handleLogicChange={(subAndIndex, subOrIndex) =>
                onRulesChange?.(
                  ERuleActionType.LOGIC_OPERATOR_CHANGE,
                  orIndex,
                  subAndIndex,
                  subOrIndex,
                )
              }
              updateActiveIndices={(...rest) => updateActiveIndices?.(orIndex, ...rest)}
              onDragEnd={onDragEnd}
              showPadding={showPadding}
            />
            {(ruleGroup?.metadata ?? []).map((andMetadata, subAndIndex) => {
              return (
                <MetadataRuleGroup
                  mode={mode}
                  key={`metadataGroup_${subAndIndex}`}
                  orIndex={orIndex}
                  subAndIndex={subAndIndex}
                  activeIndices={activeIndices}
                  data={andMetadata}
                  showPadding={showPadding}
                  updateActiveIndices={(...rest) => {
                    updateActiveIndices?.(orIndex, false, undefined, ...rest);
                  }}
                  onAddOrMetadataItem={(metadataKey) =>
                    onRulesChange?.(
                      ERuleActionType.ADD_OR_METADATA_ITEM,
                      orIndex,
                      subAndIndex,
                      -1,
                      metadataKey,
                    )
                  }
                  onDeleteMetadataGroup={() => handleDeleteMetadataGroup(orIndex, subAndIndex)}
                  onDeleteMetadataItem={(...rest) =>
                    onRulesChange?.(
                      ERuleActionType.DELETE_METADATA_ITEM,
                      orIndex,
                      subAndIndex,
                      ...rest,
                    )
                  }
                  onChange={(subOrIndex, inputType, value) =>
                    onChange?.(orIndex, subAndIndex, subOrIndex, inputType, value)
                  }
                  onDragEnd={onDragEnd}
                />
              );
            })}
            {activeOrIndex === orIndex && !isViewMode ? (
              <AddMetadataRuleButton
                disabled={disabledAddMetadataRuleButton}
                onClick={() => {
                  !disabledAddMetadataRuleButton &&
                    onRulesChange?.(ERuleActionType.ADD_METADATA_GROUP, orIndex);
                }}
              />
            ) : null}
          </Wrapper>
        );
      })}
      {showAddNewEventRuleBtn && (
        <Footer onClick={() => onRulesChange?.(ERuleActionType.ADD_RULE_GROUP)}>
          <AddRuleButton text="Add New Event Rule" textStyle={{ fontWeight: 500 }} />
        </Footer>
      )}
    </div>
  );
};

export default RuleBuilder;
